import { useState, useContext, useEffect, useRef, useMemo, useCallback } from 'react';
import { DropdownToggle, DropdownMenu, Dropdown, Input, InputGroup, InputGroupText, Button } from 'src/components/ui';
import { Trans } from '@lingui/react/macro';
import { t, plural } from '@lingui/core/macro';
import DOMPurify from 'dompurify';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import AnimateHeightBase from '../ui/AnimateHeight/AnimateHeightBase';
import InfiniteScroll from '../ui/InfiniteScroll';

import { AuthContext } from '../../context/AuthContext';
import NotificationsApi from '../../api/NotificationsApi';
import { DISABLE_NOTIFICATION_POLLING } from '../../constants';
import { DesktopNotificationPopupDispatchContext } from '../../context/DesktopNotificationPopupContext';
import Loader from '../Loader';
import { EmptyState } from '../Icons/EmptyStates';
import Icon from 'src/components/Icon';
import { useIsMobile } from '../../hooks/useIsMobile';

dayjs.extend(localizedFormat);

const POLLING_INTERVAL = 1000 * 30;
const PAGE_SIZE = 20;
const POPUP_LIMIT = 3;
const SEARCH_DEBOUNCE_MS = 400;

/** Merges a fresh first page into the accumulated list.
 *  Brand-new items (by id) are prepended; existing items are updated in-place. */
function mergeFirstPage(newItems, existing) {
  const newById = new Map(newItems.map(n => [n.id, n]));
  const existingIds = new Set(existing.map(n => n.id));
  const brandNew = newItems.filter(n => !existingIds.has(n.id));
  const updated = existing.map(n => newById.get(n.id) ?? n);
  return [...brandNew, ...updated];
}

function NavNotificationPanel() {
  const auth = useContext(AuthContext);
  const user = auth?.user?.user;
  const desktopNotificationPopupDispatch = useContext(DesktopNotificationPopupDispatchContext);
  const isMobile = useIsMobile();

  const [isOpen, setOpen] = useState(false);

  // ── Notifications state ──────────────────────────────────────────────────
  const [allNotifications, setAllNotifications] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [unreadCount, setUnreadCount] = useState(null);
  const [loadingFirst, setLoadingFirst] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingBulk, setLoadingBulk] = useState(false);

  // ── Filter state ─────────────────────────────────────────────────────────
  const [filterUnread, setFilterUnread] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const listRef = useRef(null);
  const allNotificationsRef = useRef(allNotifications);
  useEffect(() => {
    allNotificationsRef.current = allNotifications;
  }, [allNotifications]);

  const hasMore = totalCount !== null && allNotifications.length < totalCount;

  // ── Debounce search input ────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  // ── First-page polling (resets list when user or filters change) ─────────
  useEffect(() => {
    setAllNotifications([]);
    setTotalCount(null);
    setLoadingFirst(true);

    if (DISABLE_NOTIFICATION_POLLING) {
      setTotalCount(0);
      setLoadingFirst(false);
      return;
    }

    let active = true;

    const poll = async () => {
      try {
        const result = await NotificationsApi.list({
          offset: 0,
          size: PAGE_SIZE,
          unreadOnly: filterUnread || undefined,
          search: debouncedSearch || undefined,
        });
        if (!active) return;
        setAllNotifications(prev => mergeFirstPage(result.items, prev));
        setTotalCount(result.count);
        setUnreadCount(result.unreadCount);
      } finally {
        if (active) setLoadingFirst(false);
      }
    };

    poll();
    const interval = setInterval(poll, POLLING_INTERVAL);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [user, filterUnread, debouncedSearch]);

  // ── Popup sync ───────────────────────────────────────────────────────────
  useEffect(() => {
    const uncleared = allNotifications.filter(n => !n.cleared && n.isPopup).slice(0, POPUP_LIMIT);
    desktopNotificationPopupDispatch?.update(uncleared);
  }, [allNotifications]);

  // ── Load more (infinite scroll) ──────────────────────────────────────────
  const loadMore = useCallback(async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const offset = allNotificationsRef.current.length;
      const result = await NotificationsApi.list({
        offset,
        size: PAGE_SIZE,
        unreadOnly: filterUnread || undefined,
        search: debouncedSearch || undefined,
      });
      const existingIds = new Set(allNotificationsRef.current.map(n => n.id));
      const fresh = result.items.filter(n => !existingIds.has(n.id));
      setAllNotifications(prev => [...prev, ...fresh]);
      setTotalCount(result.count);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, filterUnread, debouncedSearch]);

  // ── Mark visible as read ─────────────────────────────────────────────────
  const handleMarkVisibleRead = useCallback(async () => {
    setLoadingBulk(true);
    try {
      const ids = allNotificationsRef.current.filter(n => !n.cleared).map(n => n.id);
      if (!ids.length) return;
      const newUnreadCount = await NotificationsApi.bulkMarkAsRead({ ids });
      setAllNotifications(prev => prev.map(n => (ids.includes(n.id) ? { ...n, cleared: true } : n)));
      setUnreadCount(newUnreadCount);
    } catch (e) {
      console.error(e);
    }
    setLoadingBulk(false);
  }, []);

  // ── Mark all as read (server-wide, regardless of filters/pagination) ─────
  const handleMarkAllRead = useCallback(async () => {
    setLoadingBulk(true);
    try {
      await NotificationsApi.markAllAsRead();
      setAllNotifications(prev => prev.map(n => ({ ...n, cleared: true })));
      setUnreadCount(0);
    } catch (e) {
      console.error(e);
    }
    setLoadingBulk(false);
  }, []);

  const handleMarkRead = useCallback(id => {
    try {
      NotificationsApi.markAsRead({ id });
      setUnreadCount(prev => Math.max(0, (prev ?? 1) - 1));
    } catch (e) {
      console.error(e);
    }
  }, []);

  const hasUnread = allNotifications.some(n => !n.cleared);
  const visibleUnreadCount = allNotifications.filter(n => !n.cleared).length;
  const isInitialLoad = loadingFirst && allNotifications.length === 0;
  const filtersActive = filterUnread || !!debouncedSearch;

  return (
    <Dropdown className="user-nav-dropdown" isOpen={isOpen} toggle={() => setOpen(o => !o)}>
      <DropdownToggle
        caret={false}
        tag="div"
        nav
        className={`notification-bell-toggle${isMobile ? '' : ' notification-bell-toggle--expanded'}`}
        style={{ cursor: 'pointer' }}
      >
        <span className="notification-bell-icon-wrap">
          <Icon icon="phosphor-BellDuotone" size={20} className="nav-icon modern-hover-icon" />
          {isMobile
            ? unreadCount > 0 && <span className="notification-count-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
            : unreadCount > 0 && <span className="notification-bell-dot" />}
        </span>
        {!isMobile && (
          <span className={`notification-bell-label-wrap${unreadCount > 0 ? ' has-unread' : ''}`}>
            <span className="notification-bell-label">
              {unreadCount > 0 ? plural(unreadCount, { one: '# new notification', other: '# new notifications' }) : ''}
            </span>
          </span>
        )}
      </DropdownToggle>

      <DropdownMenu end className="notification-dropdown-menu shadow p-0">
        {/* Header */}
        <div className="notification-panel-header px-3 py-2 border-bottom">
          {/* Row 1: Title + Mark all visible as read */}
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon icon="Bell" size={16} className="text-muted" />
              <span className="font-bold" style={{ fontSize: '1rem' }}>
                <Trans>Notifications</Trans>
              </span>
              {unreadCount > 0 && <small className="notification-header-badge">{unreadCount > 99 ? '99+' : unreadCount}</small>}
            </div>
            {!loadingBulk && (
              <div className="flex items-center gap-2">
                {hasUnread && (
                  <Button
                    color="link"
                    size="sm"
                    className="p-0 text-muted text-wrap"
                    style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                    onClick={handleMarkVisibleRead}
                    title={t`Mark all visible as read`}
                  >
                    <Trans>Mark visible read</Trans> ({visibleUnreadCount})
                  </Button>
                )}
                {unreadCount > visibleUnreadCount && (
                  <Button
                    color="link"
                    size="sm"
                    className="p-0 text-wrap"
                    style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                    onClick={handleMarkAllRead}
                    title={t`Mark every notification as read, including ones not currently loaded or filtered out`}
                  >
                    <Trans>Mark all read</Trans>
                  </Button>
                )}
              </div>
            )}
            {loadingBulk && (
              <div className="mr-4">
                <Loader sm dots size={10} />
              </div>
            )}
          </div>

          {/* Row 2: Filters */}
          <div className="flex items-center mt-2 w-full" style={{ gap: '6px' }}>
            <span className="text-muted" style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
              <Trans>Filters:</Trans>
            </span>
            <InputGroup size="sm" className="mb-0" style={{ flex: 1 }}>
              <InputGroupText className="bg-transparent border-0 pl-2 pr-1 text-muted">
                <Icon icon="MagnifyingGlass" style={{ fontSize: '0.75rem' }} />
              </InputGroupText>
              <Input
                type="text"
                bsSize="sm"
                className="border-0 bg-transparent shadow-none pl-0 ghost-input"
                placeholder={t`Search keywords…`}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
              {searchText && (
                <Button
                  color="link"
                  size="sm"
                  className="border-0 px-2 text-muted"
                  style={{ fontSize: '0.75rem' }}
                  onClick={() => setSearchText('')}
                  tabIndex={-1}
                >
                  <Icon icon="X" weight="regular" />
                </Button>
              )}
            </InputGroup>
            <Button
              color={filterUnread ? 'primary' : 'secondary'}
              outline={!filterUnread}
              size="sm"
              onClick={() => setFilterUnread(v => !v)}
              title={t`Show unread only`}
              style={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}
            >
              <Trans>Unread only</Trans>
            </Button>
          </div>
        </div>

        {/* Body */}
        {isInitialLoad ? (
          <div className="mt-2 mb-4 py-40">
            <Loader inline size={20}>
              <Trans>Loading notifications</Trans>
            </Loader>
          </div>
        ) : allNotifications.length === 0 ? (
          <EmptyState
            icon={filtersActive ? 'search' : 'inbox'}
            message={filtersActive ? t`No notifications match your filters` : t`No notifications to show`}
            size={120}
          />
        ) : (
          <div ref={listRef} className="notification-list">
            <InfiniteScroll
              items={allNotifications}
              renderItem={n => <NotificationItem key={n.id} notification={n} onMarkRead={() => handleMarkRead(n.id)} />}
              loadMore={loadMore}
              hasMore={hasMore}
              loading={loadingMore}
              scrollRef={listRef}
              loaderSize={24}
            />
          </div>
        )}
      </DropdownMenu>
    </Dropdown>
  );
}

function NotificationItem({ notification, onMarkRead }) {
  const { subject, message, createdDate, cleared, link } = notification;
  const [expanded, setExpanded] = useState(false);
  const [localCleared, setLocalCleared] = useState(cleared);

  useEffect(() => {
    if (cleared) setLocalCleared(true);
  }, [cleared]);

  const preview = useMemo(
    () =>
      message
        .replace(/<[^>]+>/g, ' ')
        .replace(/[\t\n\r]+/g, ' ')
        .replace(/\s{2,}/g, ' ')
        .trim(),
    [message]
  );

  const sanitizedMessage = useMemo(() => DOMPurify.sanitize(message), [message]);

  const markRead = () => {
    if (localCleared) return;
    setLocalCleared(true);
    onMarkRead?.();
  };

  const handleToggle = () => {
    if (!expanded) markRead();
    setExpanded(e => !e);
  };

  const handleDotClick = e => {
    e.stopPropagation();
    markRead();
  };

  const handleLinkClick = e => {
    e.stopPropagation();
    if (link) window.open(link, '_self');
  };

  return (
    <div className={`notification-item${!localCleared ? ' unread' : ''}`} onClick={handleToggle}>
      <div className="flex items-start gap-2">
        <span
          className={`notification-unread-dot flex-shrink-0${localCleared ? ' cleared' : ''}`}
          onClick={handleDotClick}
          title={localCleared ? undefined : t`Mark as read`}
        />

        <div className="flex-grow" style={{ minWidth: 0 }}>
          <div className="flex items-center gap-1">
            <span className="font-bold flex-grow" style={{ fontSize: '0.82rem' }}>
              {subject || <Trans>Notification</Trans>}
            </span>
            {link && (
              <button
                className="notification-link-btn btn btn-link p-0 flex-shrink-0"
                onClick={handleLinkClick}
                title={t`Open`}
                style={{ fontSize: '1.1rem' }}
              >
                <Icon icon="ShareNetwork" />
              </button>
            )}
          </div>

          <div className="text-muted" style={{ fontSize: '0.7rem' }}>
            {dayjs(createdDate).format('lll')}
          </div>

          <AnimateHeightBase height={expanded ? 0 : 'auto'} duration={200} easing="ease">
            <p className="mb-0 text-muted notification-preview-clamp mt-1" style={{ fontSize: '0.8rem' }}>
              {preview}
            </p>
          </AnimateHeightBase>

          <AnimateHeightBase height={expanded ? 'auto' : 0} duration={200} easing="ease">
            <div
              className="mt-1 notification-full-content"
              style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}
              dangerouslySetInnerHTML={{ __html: sanitizedMessage }}
            />
          </AnimateHeightBase>
        </div>
      </div>
    </div>
  );
}

export default NavNotificationPanel;
